import { useEffect } from "react";
import { Link } from "react-router-dom";
import Logo from "src/components/common/Logo";

const MERCHANT_FEE_DISCLOSURE_CONFIG = {
  pageTitle: "EatsBits Merchant Fees",
  seoTitle: "Merchant Fees | EatsBits",
  seoDescription:
    "Information about payment processing and platform fees for restaurants using EatsBits.",
  supportEmail: "support@eatsbits.com",
  lastUpdated: "July 30, 2026",
  processingRateDisplay:
    "Your applicable processing rate: Contact EatsBits or refer to your merchant agreement.",
  platformFeeDisplay:
    "Your applicable platform fee: Refer to your EatsBits agreement.",
};

const setMetaDescription = (content: string) => {
  let description = document.querySelector<HTMLMetaElement>(
    'meta[name="description"]'
  );

  if (!description) {
    description = document.createElement("meta");
    description.name = "description";
    document.head.appendChild(description);
  }

  const descriptionMeta = description;
  const previousContent = descriptionMeta.content;
  descriptionMeta.content = content;

  return () => {
    descriptionMeta.content = previousContent;
  };
};

const MerchantFees = () => {
  useEffect(() => {
    const previousTitle = document.title;
    const restoreDescription = setMetaDescription(
      MERCHANT_FEE_DISCLOSURE_CONFIG.seoDescription
    );

    window.scrollTo(0, 0);
    document.title = MERCHANT_FEE_DISCLOSURE_CONFIG.seoTitle;
    document.documentElement.classList.add("legal-page-route");
    document.body.classList.add("legal-page-route");

    return () => {
      document.title = previousTitle;
      restoreDescription();
      document.documentElement.classList.remove("legal-page-route");
      document.body.classList.remove("legal-page-route");
    };
  }, []);

  return (
    <div className="min-h-screen w-full bg-white text-[#111827]">
      <header className="border-b border-[#e5e7eb] bg-white">
        <div className="mx-auto flex max-w-screen-lg items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="flex items-center gap-2 text-lg font-semibold text-[#111827]"
          >
            <Logo />
            <span>EatsBits</span>
          </Link>
          <Link
            to="/login"
            className="text-sm font-medium text-[#FD7657] hover:underline"
          >
            Owner Login
          </Link>
        </div>
      </header>

      <main className="w-full px-4 py-6 sm:px-6 md:px-10">
        <div className="container mx-auto max-w-screen-lg p-4 sm:p-6 lg:p-8">
          <h1 className="mb-6 text-center text-2xl font-bold sm:text-3xl">
            {MERCHANT_FEE_DISCLOSURE_CONFIG.pageTitle}
          </h1>

          <p className="mb-8 text-center text-base leading-7 text-gray-600">
            EatsBits provides online ordering, enterprise meal ordering, payment
            processing integration, and related restaurant technology services.
            The fees that apply to a restaurant depend on the services selected
            and the restaurant&apos;s agreement with EatsBits.
          </p>

          <section className="mb-6 mt-8">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              Payment Processing Fees
            </h2>
            <p>
              Online card payments are subject to payment processing fees. The
              exact processing rate will be disclosed to the restaurant before
              onboarding and will be included in the restaurant&apos;s agreement
              with EatsBits.
            </p>
            <p className="mt-4 border-l-4 border-[#FD7657] bg-[#fff7f4] px-4 py-3 font-medium">
              {MERCHANT_FEE_DISCLOSURE_CONFIG.processingRateDisplay}
            </p>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              EatsBits Platform Fees
            </h2>
            <p>
              EatsBits may charge platform, software, delivery, order, or
              service fees depending on the products used by the restaurant. Any
              applicable fees will be disclosed before the restaurant begins
              accepting payments through EatsBits.
            </p>
            <p className="mt-4 border-l-4 border-[#FD7657] bg-[#fff7f4] px-4 py-3 font-medium">
              {MERCHANT_FEE_DISCLOSURE_CONFIG.platformFeeDisplay}
            </p>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              Refunds and Chargebacks
            </h2>
            <p>
              Payment processing fees, chargeback fees, refund-related fees, or
              other network fees may apply according to the restaurant&apos;s
              agreement and the rules of the payment processor and card
              networks.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">Payouts</h2>
            <p>
              Restaurant payouts are sent to the bank account provided during
              merchant onboarding. Payout timing may depend on processing
              schedules, weekends, holidays, account status, and risk review.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              Questions
            </h2>
            <p>
              For questions about pricing or fees, contact EatsBits at{" "}
              <a
                href={`mailto:${MERCHANT_FEE_DISCLOSURE_CONFIG.supportEmail}`}
                className="text-blue-500 underline"
              >
                {MERCHANT_FEE_DISCLOSURE_CONFIG.supportEmail}
              </a>
              .
            </p>
          </section>

          <footer className="border-t border-[#e5e7eb] pt-6 text-sm text-gray-600">
            <nav className="mb-4 flex flex-wrap gap-x-6 gap-y-2">
              <Link to="/terms" className="text-blue-500 underline">
                Terms of Service
              </Link>
              <Link to="/privacy" className="text-blue-500 underline">
                Privacy Policy
              </Link>
              <a
                href={`mailto:${MERCHANT_FEE_DISCLOSURE_CONFIG.supportEmail}`}
                className="text-blue-500 underline"
              >
                {MERCHANT_FEE_DISCLOSURE_CONFIG.supportEmail}
              </a>
            </nav>
            <p>Last updated: {MERCHANT_FEE_DISCLOSURE_CONFIG.lastUpdated}</p>
          </footer>
        </div>
      </main>
    </div>
  );
};

export default MerchantFees;
